import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, Lock, Shield, Eye, Mail, Phone, MapPin, ArrowRight, CheckCircle2, Sparkles } from 'lucide-react';
import {
  Container,
  Section,
  SectionHeader
} from './modern/Layout';
import { ModernButton, ButtonGroup } from './modern/ModernButton';
import { FadeIn, StaggerContainer, StaggerItem } from './animations/MotionComponents';

const Privacy = () => {
  const [expandedSection, setExpandedSection] = useState(0);

  const privacySections = [
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Information We Collect',
      description: 'Overview of data collection practices',
      color: 'from-blue-500 to-blue-600',
      content: [
        'Personal Information: Name, email address, phone number, company details',
        'Technical Data: IP address, browser type, device information, cookies',
        'Usage Information: Pages visited, time spent, interactions, preferences',
        'Communication Data: Messages, feedback, support inquiries'
      ]
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'How We Use Your Data',
      description: 'Data usage and purposes',
      color: 'from-cyan-500 to-cyan-600',
      content: [
        'Service Delivery: Provide and improve our services',
        'Communication: Send updates, newsletters, promotional content',
        'Analytics: Understand user behavior and optimize platform',
        'Security: Detect fraud and maintain platform integrity',
        'Legal Compliance: Meet regulatory and legal requirements'
      ]
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Data Security',
      description: 'Protection measures and encryption',
      color: 'from-purple-500 to-purple-600',
      content: [
        'End-to-End Encryption: All sensitive data encrypted in transit and at rest',
        'SSL/TLS Protocol: Secure connection for all data transfers',
        'Regular Audits: Security assessments and vulnerability testing',
        'Access Control: Role-based access with multi-factor authentication',
        'Data Backup: Regular backups with disaster recovery procedures'
      ]
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Your Privacy Rights',
      description: 'Rights and user controls',
      color: 'from-pink-500 to-pink-600',
      content: [
        'Access: Request and download your personal data',
        'Correction: Update or modify inaccurate information',
        'Deletion: Request permanent data removal (Right to be Forgotten)',
        'Opt-Out: Unsubscribe from communications at any time',
        'Portability: Receive data in machine-readable format'
      ]
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Third-Party Sharing',
      description: 'Data sharing policies',
      color: 'from-orange-500 to-orange-600',
      content: [
        'Service Providers: Shared with vendors for operational purposes',
        'Legal Requirements: Disclosed when required by law',
        'Business Transfers: May transfer data in case of merger/acquisition',
        'No Sale: We never sell personal data to third parties',
        'Consent: Explicit permission required for non-essential sharing'
      ]
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Global Compliance',
      description: 'International data regulations',
      color: 'from-green-500 to-green-600',
      content: [
        'GDPR: EU General Data Protection Regulation compliance',
        'CCPA: California Consumer Privacy Act compliance',
        'HIPAA: Healthcare data protection standards',
        'SOC 2: System and Organization Controls certification',
        'Data Residency: Data stored in compliant jurisdictions'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-blue-50 to-white">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-100 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-cyan-100 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-72 h-72 bg-purple-100 rounded-full mix-blend-screen filter blur-3xl opacity-20 animate-pulse delay-4000"></div>
      </div>

      {/* Hero Section */}
      <Section background="transparent" padding="medium">
        <Container>
          <FadeIn>
            <div className="max-w-4xl mx-auto text-center space-y-6 relative z-10">
              <motion.div
                initial={{ scale: 0.5, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="flex justify-center"
              >
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-2xl blur-2xl opacity-50"></div>
                  <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg">
                    <Lock className="w-10 h-10 text-white" />
                  </div>
                </div>
              </motion.div>

              <div className="space-y-4">
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="text-blue-600 font-semibold flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Privacy & Security First
                </motion.p>

                <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
                  Your Privacy <br />
                  <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-400 bg-clip-text text-transparent">Is Sacred</span>
                </h1>
              </div>

              <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
                We're transparent about how we collect, use, and protect your data. Your trust is our most valuable asset.
              </p>

              <div className="flex justify-center gap-4 pt-4">
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                >
                  <button className="px-8 py-3 rounded-full bg-white/10 backdrop-blur border border-white/20 text-white hover:bg-white/20 transition-all">
                    Learn More ↓
                  </button>
                </motion.div>
              </div>

              <div className="text-xs text-gray-500 pt-2">
                Last Updated: January 16, 2026
              </div>
            </div>
          </FadeIn>
        </Container>
      </Section>

      {/* Key Metrics Section */}
      <Section background="transparent" padding="small">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
            {[
              { icon: '🔐', title: 'Military-Grade', desc: 'AES-256 Encryption' },
              { icon: '✓', title: 'Fully Compliant', desc: 'GDPR • CCPA • HIPAA' },
              { icon: '🛡️', title: 'Verified Secure', desc: 'SOC 2 Type II' }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="relative group"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-blue-100/50 to-cyan-100/50 rounded-2xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="relative p-6 rounded-2xl bg-white border border-blue-100 hover:border-blue-200 hover:shadow-lg transition-all">
                  <div className="text-4xl mb-1">{item.icon}</div>
                  <h3 className="font-bold text-base text-gray-900 mb-0.5">{item.title}</h3>
                  <p className="text-xs text-gray-700">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Privacy Sections */}
      <Section background="transparent" padding="small">
        <Container>
          <div className="max-w-4xl mx-auto">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-2"
            >
              Privacy Details
            </motion.h2>
            <p className="text-center text-gray-600 mb-6">
              Comprehensive information about our privacy practices
            </p>

            <div className="space-y-3">
              {privacySections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                >
                  <motion.button
                    onClick={() => setExpandedSection(expandedSection === index ? -1 : index)}
                    className="w-full text-left group"
                    whileHover={{ scale: 1.02 }}
                  >
                    <div className="relative overflow-hidden">
                      <div className={`absolute inset-0 bg-gradient-to-r ${section.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300 rounded-2xl`}></div>
                      <div className="relative bg-white border border-gray-200 hover:border-blue-300 group-hover:bg-blue-50 transition-all rounded-2xl p-5">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 flex-1">
                            <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${section.color} flex items-center justify-center text-white flex-shrink-0 shadow-lg`}>
                              {section.icon}
                            </div>
                            <div className="text-left">
                              <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                                {section.title}
                              </h3>
                              <p className="text-sm text-gray-700 mt-0.5">
                                {section.description}
                              </p>
                            </div>
                          </div>
                          <motion.div
                            animate={{ rotate: expandedSection === index ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <ChevronDown className="w-6 h-6 text-blue-500 flex-shrink-0" />
                          </motion.div>
                        </div>

                        <AnimatePresence>
                          {expandedSection === index && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                              className="mt-4 pt-4 border-t border-gray-200"
                            >
                              <ul className="space-y-2">
                                {section.content.map((item, i) => (
                                  <motion.li
                                    key={i}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="flex items-start gap-2"
                                  >
                                    <CheckCircle2 className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                                    <span className="text-gray-700">{item}</span>
                                  </motion.li>
                                ))}
                              </ul>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </div>
        </Container>
      </Section>

      {/* Contact Section */}
      <Section background="transparent" padding="medium">
        <Container>
          <FadeIn>
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-100/20 to-cyan-100/20 rounded-3xl blur-2xl"></div>
                <div className="relative bg-white/80 border border-blue-100 rounded-3xl p-6 space-y-4">
                  <h2 className="text-2xl font-bold text-gray-900 text-center">
                    Privacy Questions?
                  </h2>
                  <p className="text-center text-gray-700">
                    Our data protection team is ready to help clarify any concerns about our privacy practices.
                  </p>

                  <div className="space-y-2">
                    <div className="flex gap-3 p-3 rounded-xl bg-white/80 hover:bg-blue-50 transition-all border border-blue-100">
                      <Mail className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                        <p className="text-gray-700">privacy@nehainfotech.com</p>
                      </div>
                    </div>
                    <div className="flex gap-3 p-3 rounded-xl bg-white/80 hover:bg-blue-50 transition-all border border-blue-100">
                      <Phone className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                        <p className="text-gray-700">+1 (555) 123-4567</p>
                      </div>
                    </div>
                  </div>

                  <ButtonGroup className="pt-2">
                    <Link to="/contact">
                      <ModernButton variant="primary" size="lg" icon={<ArrowRight />}>
                        Contact Privacy Team
                      </ModernButton>
                    </Link>
                    <Link to="/">
                      <ModernButton variant="outline" size="lg">
                        Back to Home
                      </ModernButton>
                    </Link>
                  </ButtonGroup>
                </div>
              </div>
            </div>
          </FadeIn>
        </Container>
      </Section>
    </div>
  );
};

export default Privacy;
